// R116 #1 "Welcome home" — the drawn art shared by every sign-in screen, the
// boot splash (client/index.html carries a byte-identical copy of
// skylineSvg(); scripts/test_login_dom.mjs pins the two together) and the one
// brand mark (the same house as client/public/icon.svg and the sidebar logo).
// Pure string builders: deterministic (no randomness, no clock), no DOM.
// Colours never live here — every fill/stroke is a class styled in auth.css
// from the app's :root tokens.

/** The brand mark: a blue tile with a white house whose window can light up
 *  and whose door can swing open (auth.css .au-mark.is-lit / .is-open). */
export function brandMarkSvg(size = 56): string {
  return `<svg class="au-mk" viewBox="0 0 48 48" width="${size}" height="${size}" aria-hidden="true" focusable="false">`
    + `<rect class="au-mk-bg" width="48" height="48" rx="13"/>`
    + `<rect class="au-mk-in" x="16.6" y="27.2" width="5.8" height="9.8"/>`
    + `<rect class="au-mk-door" x="16.6" y="27.2" width="5.8" height="9.8" rx=".6"/>`
    + `<rect class="au-mk-win" x="26.4" y="23.4" width="6.4" height="6.2" rx="1.1"/>`
    + `<path class="au-mk-line au-mk-walls" pathLength="1" d="M12 19.8V37h24V19.8"/>`
    + `<path class="au-mk-line au-mk-roof" pathLength="1" d="M8 23 24 9.5 40 23"/>`
    + `</svg>`;
}

type House = { x: number; w: number; h: number; r: number; chimney?: boolean; door?: 'l' | 'r' | 'c' };
type Tree = { x: number; r: number };

// One street, 1440 units wide. Phones see the middle (~437–1003) at a
// readable size (the svg is sliced, not squeezed), so the nicest houses sit
// there. 'd' windows are lit by day, 'n' windows join them at dusk.
const G = 166;                       // ground line
const HOUSES: House[] = [
  { x: 22, w: 92, h: 56, r: 30, door: 'l' },
  { x: 130, w: 126, h: 92, r: 40, chimney: true },
  { x: 318, w: 86, h: 60, r: 34, door: 'r' },
  { x: 420, w: 112, h: 96, r: 38 },
  { x: 548, w: 78, h: 52, r: 28, door: 'c' },
  { x: 676, w: 132, h: 104, r: 44, chimney: true },
  { x: 826, w: 94, h: 62, r: 32, door: 'l' },
  { x: 972, w: 116, h: 88, r: 40 },
  { x: 1104, w: 80, h: 54, r: 30, door: 'r' },
  { x: 1200, w: 124, h: 96, r: 42, chimney: true },
  { x: 1382, w: 62, h: 48, r: 26, door: 'c' },
];
const TREES: Tree[] = [{ x: 286, r: 19 }, { x: 650, r: 16 }, { x: 946, r: 18 }, { x: 1354, r: 15 }];
// window index → lit by day ('d') or from dusk ('n'); the rest stay dark until the timer / a click
const DAY = new Set([2, 9, 15, 22, 28]);
const DUSK = new Set([0, 4, 6, 11, 13, 17, 19, 24, 26, 31]);

const n1 = (v: number) => (Math.round(v * 10) / 10).toString();

/** The neighbourhood along the bottom of the stage. */
export function skylineSvg(): string {
  let wi = 0;
  const win = (x: number, y: number, w: number, h: number) => {
    const i = wi++;
    const cls = DAY.has(i) ? 'au-w d' : DUSK.has(i) ? 'au-w n' : 'au-w';
    return `<rect class="${cls}" data-w="${i}" style="--i:${i}" x="${n1(x)}" y="${n1(y)}" width="${w}" height="${h}" rx="2"/>`;
  };
  const parts: string[] = [];
  for (const t of TREES) {
    parts.push(`<rect class="au-trunk" x="${n1(t.x - 2)}" y="${G - 22}" width="4" height="22" rx="1"/>`
      + `<circle class="au-tree" cx="${t.x}" cy="${n1(G - 22 - t.r * 0.8)}" r="${t.r}"/>`);
  }
  for (const hs of HOUSES) {
    const top = G - hs.h, apex = top - hs.r, mid = hs.x + hs.w / 2;
    let s = '';
    if (hs.chimney) {
      const cx = hs.x + hs.w * 0.72;
      const roofY = apex + (hs.r * (cx - mid)) / (hs.w / 2);
      s += `<rect class="au-h" x="${n1(cx)}" y="${n1(roofY - 16)}" width="12" height="20" rx="1"/>`;
    }
    s += `<path class="au-h" d="M${n1(hs.x)} ${G}V${top}L${n1(mid)} ${apex}L${n1(hs.x + hs.w)} ${top}V${G}Z"/>`;
    s += `<path class="au-roof" d="M${n1(hs.x - 7)} ${n1(top + 6)}L${n1(mid)} ${apex}L${n1(hs.x + hs.w + 7)} ${n1(top + 6)}"/>`;
    // attic window in tall gables
    if (hs.r >= 38) s += win(mid - 5, apex + hs.r * 0.48, 10, 10);
    const cols = Math.max(1, Math.floor((hs.w - 22) / 30));
    const slot = hs.w / cols;
    const rows = hs.h >= 80 ? [top + 14, top + 48] : [top + hs.h * 0.3];
    rows.forEach((y, ri) => {
      const ground = ri === rows.length - 1;
      for (let c = 0; c < cols; c++) {
        const doorHere = ground && hs.door && ((hs.door === 'l' && c === 0) || (hs.door === 'r' && c === cols - 1) || (hs.door === 'c' && cols === 1));
        const cx = hs.x + slot * c + slot / 2;
        if (doorHere) s += `<rect class="au-door" x="${n1(cx - 7)}" y="${G - 26}" width="14" height="26" rx="2"/>`;
        else s += win(cx - 7, y, 14, 16);
      }
    });
    parts.push(s);
  }
  parts.push(`<path class="au-ground" d="M0 ${G + 0.5}H1440"/>`);
  return `<svg class="au-sky-svg" viewBox="0 0 1440 170" preserveAspectRatio="xMidYMax slice" width="1440" height="170" aria-hidden="true" focusable="false">${parts.join('')}</svg>`;
}

/** How many windows the skyline has (authFx sequences over them). */
export const WINDOW_COUNT = (() => { const m = skylineSvg().match(/class="au-w/g); return m ? m.length : 0; })();
export const DAY_WINDOWS = [...DAY];
export const DUSK_WINDOWS = [...DUSK];
